import { Button } from '@ads/components-react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ClassPlanningFilterBar,
  ClassPlanningListTemplate,
  ClassPlanningTable,
  useClassPlanningFilters,
  useClassPlanningList,
  useExportClassPlanningCsv,
} from '@/features/class-planning';

export const Route = createFileRoute('/_authenticated/class-planning/')({
  component: ClassPlanningListPage,
});

/** Página de listagem de planejamento de aulas. */
export function ClassPlanningListPage() {
  const { t } = useTranslation('classPlanning');

  const { data, isLoading, currentPage, hasNextPage, totalPages, totalCount } =
    useClassPlanningList();
  const { filters, updateFilters, resetFilters } = useClassPlanningFilters();
  const { exportCsv, isExporting } = useExportClassPlanningCsv();

  // Só a limpeza no unmount importa aqui — `resetFilters` fecha sobre o setter
  // estável do Jotai, não sobre um valor que possa ficar obsoleto. Resetar no
  // unmount (não no mount) evita um fetch duplicado: ao voltar a esta página,
  // o átomo já está em `INITIAL_CLASS_PLANNING_FILTERS` desde o primeiro render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ver comentário acima
  useEffect(() => {
    return () => resetFilters();
  }, []);

  return (
    <ClassPlanningListTemplate
      exportAction={
        <Button
          hierarchy="primary"
          leadingIcon={ArrowDownTrayIcon}
          isLoading={isExporting}
          disabled={data.length === 0}
          onClick={() => void exportCsv()}
        >
          {t('list.exportExcel')}
        </Button>
      }
      filterPanel={<ClassPlanningFilterBar />}
      table={
        <ClassPlanningTable
          data={data}
          isLoading={isLoading}
          currentPage={currentPage}
          hasNextPage={hasNextPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={page => updateFilters({ page })}
          sortField={filters.sortField}
          sortDirection={filters.sortDirection}
          onSortChange={(sortField, sortDirection) =>
            updateFilters({ sortField, sortDirection, page: 1 })
          }
        />
      }
    />
  );
}
